import { Shapes } from '@/components/public/Visualisations/types'
import { XYBubbleCircle } from '@/components/public/Visualisations/XYBubbles/Entities/XYBubbleCircle'
import { XYBubbleHaloCircle } from '@/components/public/Visualisations/XYBubbles/Entities/XYBubbleHaloCircle'
import { XYBubblePulseCircle } from '@/components/public/Visualisations/XYBubbles/Entities/XYBubblePulseCircle'
import { highlightedPathNotifier } from '@/components/public/Visualisations/XYBubbles/highlightedPathNotifier'
import { XYBubbleMetadata } from '@/components/public/Visualisations/XYBubbles/Metadata/XYBubbleMetadata'
import Konva from 'konva'

const OPACITY = 0.7
const HIGHLIGHTED_OPACITY = 1

export class XYBubbleGroup extends Konva.Group implements Shapes.Group {
  private _xyBubbleMetadata: XYBubbleMetadata
  private _isHighlighted: boolean = false
  private boundOnHighlightedChange = this.onHighlightedChange.bind(this)
  capabilities: Shapes.Capability[] = [Shapes.Capability.Fadable]
  entities: Shapes.Entity[] = []
  _bubble: XYBubbleCircle
  _halo: XYBubbleHaloCircle
  _pulse: XYBubblePulseCircle

  constructor(bubbleMetadata: XYBubbleMetadata) {
    super()
    this._xyBubbleMetadata = bubbleMetadata

    this._bubble = new XYBubbleCircle(bubbleMetadata)
    this.addShape(this._bubble)
    this._halo = new XYBubbleHaloCircle(bubbleMetadata.haloMetadata)
    this.addShape(this._halo)
    this._pulse = new XYBubblePulseCircle(bubbleMetadata.pulseMetadata)
    this.addShape(this._pulse)

    this.position(bubbleMetadata.destinationPosition)

    // When the d3 simulation updates the Bubbles coordinates we also need to update the
    // Konva Circles, the most efficient way to do this is to pass a callback to each Bubble
    // that will update the Circle coordinates every time the Bubble coordinates are updated
    bubbleMetadata.onPositionChanged((position) => {
      this.position(position)
    })

    highlightedPathNotifier.subscribe(this.path, this.boundOnHighlightedChange)

    this._xyBubbleMetadata = bubbleMetadata
    this.refresh()
  }

  get key() {
    return this._xyBubbleMetadata.key
  }

  get path() {
    return this._xyBubbleMetadata.path
  }

  get center() {
    return this.position()
  }

  refresh() {
    this.updateOpacity()
    this._bubble.refresh()
    this._halo.refresh()
    this._pulse.refresh()
  }

  destroy() {
    highlightedPathNotifier.unsubscribe(this.path, this.boundOnHighlightedChange)

    super.destroy()
    return this
  }

  onAdded() {
    this.updateZIndex()
  }

  private onHighlightedChange(isHighlighted: boolean) {
    this._isHighlighted = isHighlighted

    this.updateOpacity()
  }

  private updateZIndex() {
    if (this.parent) {
      this.zIndex(Math.min(this._xyBubbleMetadata.zIndex, this.parent.children.length - 1))
    }
  }

  private updateOpacity() {
    const opacity = this._isHighlighted ? HIGHLIGHTED_OPACITY : OPACITY
    this._bubble.opacity(opacity)
    this._halo.opacity(opacity)
    this._pulse.opacity(opacity)
  }

  private addShape(shape: Shapes.Shape) {
    this.entities.push(shape)
    this.add(shape)
  }
}
